import { logoutAction } from '@Features/Authentication/AuthenticationSlice/Authentication.slice'
import { saveReturnUrl } from '@Utilities/returnUrl'
import routes from '@Constants/Routes'

/**
 * Machine-readable reasons a session ended, mirroring the backend's
 * `data.code`. Follows the SequentialLockException precedent — the only
 * existing place the API returns a code the client can branch on.
 */
export const SESSION_CODES = {
	IDLE_EXPIRED: 'SESSION_IDLE_EXPIRED',
	REVOKED: 'SESSION_REVOKED',
	SIGNED_IN_ELSEWHERE: 'SESSION_SIGNED_IN_ELSEWHERE',
	DEVICE_REVOKED: 'DEVICE_REVOKED',
	/**
	 * Signed in, but the browser has not completed its device challenge.
	 *
	 * Deliberately NOT terminal: the session is live and valid, the device just
	 * is not trusted yet. Signing them out would destroy the very session the
	 * verification endpoint needs. Handled below by routing to the challenge.
	 */
	DEVICE_UNVERIFIED: 'DEVICE_UNVERIFIED',
	PASSWORD_CHANGED: 'PASSWORD_CHANGED',
} as const

// No DEVICE_LIMIT. Being at the limit is a 403 during login carrying its own
// code, handled by the at-limit screen — never a 401 on a live session. The old
// entry here was spelled 'DEVICE_LIMIT_REACHED' while Login.tsx matched
// 'DEVICE_LIMIT', so the two could never both be right.

/**
 * Not every 401 means "your session died".
 *
 * A wrong password at login is a 401. An unauthenticated /auth/me on a cold
 * boot is a 401. Treating those as terminal would redirect the user away from
 * the login form they are currently using, and overwrite the return URL they
 * were heading for — a login loop.
 *
 * So only 401s that carry a session-invalidation code are terminal. Anything
 * else is left to the calling component to render as an ordinary error.
 */
/**
 * Exported for the login page: these are exactly the codes that can arrive on
 * /login as `?reason=`, so the banner renders for these and nothing else — an
 * unknown value in an attacker-writable query param must render nothing, not
 * a raw translation key.
 */
export const TERMINAL_CODES: string[] = Object.values(SESSION_CODES).filter(code => code !== SESSION_CODES.DEVICE_UNVERIFIED)

/**
 * Fire at most once per navigation.
 *
 * A single page can have a dozen queries in flight; without this the student
 * would get a dozen redirects and a dozen snackbars for one expiry.
 */
let handledForPath: string | null = null

/**
 * Called after a successful sign-in.
 *
 * The guard keys on the current path, which covers a burst of parallel 401s but
 * not a second expiry on the SAME path after signing back in — that one was
 * swallowed, leaving the user on a page whose every request 401s with no
 * redirect. Signing in is the unambiguous "that expiry is dealt with" moment.
 */
export const resetUnauthorizedGuard = () => {
	handledForPath = null
}

export const handleUnauthorized = (dispatch: (action: any) => void, error: unknown): void => {
	const code = (error as any)?.data?.data?.code as string | undefined

	// The session is fine; the DEVICE is not verified yet. Everything except the
	// challenge endpoints returns this, so without handling it the app rendered
	// as signed in — bootstrap succeeds, /auth/me is allowlisted — while every
	// other request failed, with nothing sending the student anywhere. Route to
	// the challenge instead, and do NOT clear auth: the verification call needs
	// this session.
	if (code === SESSION_CODES.DEVICE_UNVERIFIED) {
		if (window.location.pathname !== routes.verifyDevice) {
			// Remember where they were headed — in sessionStorage, NOT Redux. The
			// assign() below is a full document load and the fresh document boots a
			// fresh store, so the old dispatch(setCurrentURL) filled state that died
			// microseconds later: a student who opened a deep link to a lesson on a
			// new browser verified successfully and then landed on the home page,
			// having lost the thing they clicked. VerifyDevice consumes this.
			const path = `${window.location.pathname}${window.location.search}`
			if (!path.startsWith(routes.login)) saveReturnUrl(path)
			window.location.assign(routes.verifyDevice)
		}
		return
	}

	// Not a session death — e.g. wrong credentials, or a boot-time /auth/me on a
	// visitor who was never signed in. Let the caller handle it.
	//
	// The `!code` half is load-bearing and was missing: `code && ...`short-circuits
	// to false when there is no code at all, so a plain 401 fell THROUGH into the
	// terminal path. Every anonymous visitor hit /auth/me on boot, got a
	// code-less 401, and was redirected off the public landing page to
	// /login?reason=Your%20session%20expired. A wrong password did the same,
	// wiping the form mid-typing.
	if (!code || !TERMINAL_CODES.includes(code)) return

	const path = `${window.location.pathname}${window.location.search}`
	if (handledForPath === path) return
	handledForPath = path

	// Capture where they were so login can return them there — in
	// sessionStorage, because the assign() below reloads the document and a
	// Redux write here dies with it. That was the whole bug: the capture ran,
	// the store held the URL for microseconds, and login always read undefined.
	if (path && !path.startsWith(routes.login)) {
		saveReturnUrl(path)
	}

	dispatch(logoutAction())

	// Go to login, not home. Landing on the marketing page with no explanation
	// was the single worst part of the old behaviour.
	//
	// The CODE rides the URL, not prose: the query string is the one thing that
	// survives the reload, and the login page translates the code in the
	// student's own language. The old version encoded an English sentence here
	// that nothing ever rendered.
	window.location.assign(`${routes.login}?reason=${encodeURIComponent(code)}`)
}
